import { lazy, Suspense, ReactNode, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ComparisonProvider } from "@/contexts/ComparisonContext";
import { CookieConsent } from "@/components/CookieConsent";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PageSkeleton } from "@/components/ui/PageSkeleton";

import { usePageTracking } from "@/hooks/usePageTracking";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import { useAdminAuth } from "@/hooks/useAdminAuth";

// Eager load homepage only for instant FCP
import Index from "./pages/Index";

// Lazy load pages with preload capability - imported from separate file to avoid circular deps
import {
  ContactPage,
  MortgagePage,
  PrivateLoanPage,
  BusinessLoanPage,
  KlarsprakPage,
  LoanWithoutUCPage,
  MortgageCalculatorPage,
  PrivateLoanCalculatorPage,
  InterestRateCalculatorPage,
  KALPCalculatorPage
} from "./routes/preloadableRoutes";

const FAQPage = lazy(() => import("./pages/FAQPage"));

// Lazy load less frequently accessed pages
const MaintenancePage = lazy(() => import("./pages/MaintenancePage"));
const SavingsPage = lazy(() => import("./pages/SavingsPage"));
const CreditCardPage = lazy(() => import("./pages/CreditCardPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ToolsPage = lazy(() => import("./pages/ToolsPage"));
const FerraturmPage = lazy(() => import("./pages/FerraturmPage"));
const FairloPage = lazy(() => import("./pages/FairloPage"));
const MeraxPage = lazy(() => import("./pages/MeraxPage"));
const FlexkontotPage = lazy(() => import("./pages/FlexkontotPage"));
const BrixioPage = lazy(() => import("./pages/BrixioPage"));
const LoanstepPage = lazy(() => import("./pages/LoanstepPage"));
const LendoForetagslanPage = lazy(() => import("./pages/LendoForetagslanPage"));
const FedeltaPage = lazy(() => import("./pages/FedeltaPage"));
const KreaPage = lazy(() => import("./pages/KreaPage"));
const SamblaPage = lazy(() => import("./pages/SamblaPage"));
const LendoPage = lazy(() => import("./pages/LendoPage"));
const ThornPage = lazy(() => import("./pages/ThornPage"));
const Plus1Page = lazy(() => import("./pages/Plus1Page"));
const AnyfinPage = lazy(() => import("./pages/AnyfinPage"));
const EnklarePage = lazy(() => import("./pages/EnklarePage"));
const ZensumPage = lazy(() => import("./pages/ZensumPage"));

const Lanekoll2Page = lazy(() => import("./pages/Lanekoll2Page"));
const AdvisaPage = lazy(() => import("./pages/AdvisaPage"));
const TryggaPage = lazy(() => import("./pages/TryggaPage"));
const QredPage = lazy(() => import("./pages/QredPage"));
const OPRForetagslanPage = lazy(() => import("./pages/OPRForetagslanPage"));
const FrodaPage = lazy(() => import("./pages/FrodaPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const BankDetailsPage = lazy(() => import("./pages/BankDetailsPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const AffiliateDisclosurePage = lazy(() => import("./pages/AffiliateDisclosurePage"));
const ArshaJosefssonPage = lazy(() => import("./pages/ArshaJosefssonPage"));
const OrdnaBolanPage = lazy(() => import("./pages/OrdnaBolanPage"));
const Offline = lazy(() => import("./pages/Offline"));

// Lazy load guide pages
const AmortizationPage = lazy(() => import("./pages/guides/AmortizationPage"));
const AnnuitetslanPage = lazy(() => import("./pages/guides/AnnuitetslanPage"));
const BankkortKreditkortPage = lazy(() => import("./pages/guides/BankkortKreditkortPage"));
const BelaningsgradPage = lazy(() => import("./pages/guides/BelaningsgradPage"));
const BetalkortPage = lazy(() => import("./pages/guides/BetalkortPage"));
const BorgensmanPage = lazy(() => import("./pages/guides/BorgensmanPage"));
const BorgenarPage = lazy(() => import("./pages/guides/BorgenarPage"));
const BudgetkalylPage = lazy(() => import("./pages/guides/BudgetkalylPage"));
const EffektivRantaPage = lazy(() => import("./pages/guides/EffektivRantaPage"));
const ForetagslanAktiebolagPage = lazy(() => import("./pages/guides/ForetagslanAktiebolagPage"));
const GavobrevPage = lazy(() => import("./pages/guides/GavobrevPage"));
const GaldenarPage = lazy(() => import("./pages/guides/GaldenarPage"));
const HandpenningPage = lazy(() => import("./pages/guides/HandpenningPage"));
const HurMycketKanJagLanaPage = lazy(() => import("./pages/guides/HurMycketKanJagLanaPage"));
const ISKPage = lazy(() => import("./pages/guides/ISKPage"));
const InflationPage = lazy(() => import("./pages/guides/InflationPage"));
const KALPPage = lazy(() => import("./pages/guides/KALPPage"));
const KlarnaKopNuBetalaSenare = lazy(() => import("./pages/guides/KlarnaKopNuBetalaSenare"));
const KontantinsatsPage = lazy(() => import("./pages/guides/KontantinsatsPage"));
const KontantkortPrepaidPage = lazy(() => import("./pages/guides/KontantkortPrepaidPage"));
const KopebrevPage = lazy(() => import("./pages/guides/KopebrevPage"));
const KopekontraktPage = lazy(() => import("./pages/guides/KopekontraktPage"));
const KreditupplysningPage = lazy(() => import("./pages/guides/KreditupplysningPage"));
const LagfartPage = lazy(() => import("./pages/guides/LagfartPage"));
const LanUtanSakerhetPage = lazy(() => import("./pages/guides/LanUtanSakerhetPage"));
const LanaUtanUCPage = lazy(() => import("./pages/guides/LanaUtanUCPage"));
const LanekalylPage = lazy(() => import("./pages/guides/LanekalylPage"));
const LaneloftePage = lazy(() => import("./pages/guides/LaneloftePage"));
const LaneskyddPage = lazy(() => import("./pages/guides/LaneskyddPage"));
const OverbryggninslanPage = lazy(() => import("./pages/guides/OverbryggninslanPage"));
const PantbrevPage = lazy(() => import("./pages/guides/PantbrevPage"));
const RantaRorligEllerBundenPage = lazy(() => import("./pages/guides/RantaRorligEllerBundenPage"));
const RanteavdragPage = lazy(() => import("./pages/guides/RanteavdragPage"));
const RanterabattPage = lazy(() => import("./pages/guides/RanterabattPage"));
const RefinsieringPage = lazy(() => import("./pages/guides/RefinsieringPage"));
const ReporantaPage = lazy(() => import("./pages/guides/ReporantaPage"));
const SamlingslanPage = lazy(() => import("./pages/guides/SamlingslanPage"));
const SkuldsaneringPage = lazy(() => import("./pages/guides/SkuldsaneringPage"));
const SnittrantaPage = lazy(() => import("./pages/guides/SnittrantaPage"));
const SwishPage = lazy(() => import("./pages/guides/SwishPage"));
const UpskovPage = lazy(() => import("./pages/guides/UpskovPage"));

const queryClient = new QueryClient();

const AppContent = () => {
  usePageTracking();
  return null;
};

const MaintenanceWrapper = ({ children }: { children: React.ReactNode }) => {
  const { isMaintenanceMode, isLoading: maintenanceLoading } = useMaintenanceMode();
  const { isAdmin, loading: adminLoading } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Don't redirect while loading
    if (maintenanceLoading || adminLoading) return;
    
    const isMaintenancePage = location.pathname === '/maintenance';
    const isAdminRoute = location.pathname === '/admin' || location.pathname.includes('/admin');
    const isAuthRoute = location.pathname === '/auth';
    
    // Redirect to maintenance if enabled and user is not admin
    if (isMaintenanceMode && !isAdmin && !isMaintenancePage && !isAdminRoute && !isAuthRoute) {
      navigate('/maintenance', { replace: true });
    }
    
    // Redirect away from maintenance if disabled
    if (!isMaintenanceMode && isMaintenancePage) {
      navigate('/sv-SE', { replace: true });
    }
  }, [isMaintenanceMode, isAdmin, maintenanceLoading, adminLoading, location.pathname, navigate]);
  
  // Show loading while checking maintenance and admin status
  if (maintenanceLoading || adminLoading) return <PageSkeleton />;
  
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <ScrollToTop />
      <LanguageProvider>
        <CookieConsentProvider>
          <AuthProvider>
            <ComparisonProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <CookieConsent />
                <AppContent />
                <ErrorBoundary fallback={<PageSkeleton />}>
                  <MaintenanceWrapper>
                    <Suspense fallback={<PageSkeleton />}>
                      <Routes>
                    <Route path="/" element={<Navigate to="/sv-SE" replace />} />
                    
                    <Route path="/maintenance" element={<MaintenancePage />} />
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/:lang/admin" element={<AdminPage />} />
                    <Route path="/blog" element={<BlogListPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/cookies" element={<CookiePolicy />} />
                    <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
                    <Route path="/offline" element={<Offline />} />
                    <Route path="/:lang" element={<Index />} />
                    <Route path="/:lang/bolan" element={<MortgagePage />} />
                    <Route path="/:lang/bolan/ordna-bolan" element={<OrdnaBolanPage />} />
                    <Route path="/:lang/banker/:bankSlug" element={<BankDetailsPage />} />
                    <Route path="/:lang/privatlan" element={<PrivateLoanPage />} />
            <Route path="/:lang/privatlan/sambla" element={<SamblaPage />} />
            <Route path="/:lang/privatlan/lendo" element={<LendoPage />} />
            <Route path="/:lang/privatlan/thorn" element={<ThornPage />} />
            <Route path="/:lang/privatlan/plus1" element={<Plus1Page />} />
            <Route path="/:lang/privatlan/anyfin" element={<AnyfinPage />} />
            <Route path="/:lang/privatlan/enklare" element={<EnklarePage />} />
            <Route path="/:lang/privatlan/zensum" element={<ZensumPage />} />
            <Route path="/:lang/privatlan/lanekoll" element={<Navigate to="/sv-SE/privatlan/lanekoll2" replace />} />
            <Route path="/:lang/privatlan/lanekoll2" element={<Lanekoll2Page />} />
            <Route path="/:lang/privatlan/advisa" element={<AdvisaPage />} />
            <Route path="/:lang/privatlan/trygga" element={<TryggaPage />} />
                    <Route path="/:lang/foretagslan" element={<BusinessLoanPage />} />
                    <Route path="/:lang/foretagslan/lendo" element={<LendoForetagslanPage />} />
                    <Route path="/:lang/foretagslan/fedelta" element={<FedeltaPage />} />
                    <Route path="/:lang/foretagslan/krea" element={<KreaPage />} />
                    <Route path="/:lang/foretagslan/qred" element={<QredPage />} />
                    <Route path="/:lang/foretagslan/opr-foretagslan" element={<OPRForetagslanPage />} />
                    <Route path="/:lang/foretagslan/froda" element={<FrodaPage />} />
                    <Route path="/:lang/kreditkort" element={<Navigate to="/sv-SE" replace />} />
                    <Route path="/:lang/sparkonto" element={<Navigate to="/sv-SE" replace />} />
                    <Route path="/:lang/jamfor" element={<ComparePage />} />
                    <Route path="/:lang/lana-utan-uc" element={<LoanWithoutUCPage />} />
                    <Route path="/:lang/lana-utan-uc/ferratum" element={<FerraturmPage />} />
            <Route path="/:lang/lana-utan-uc/fairlo" element={<FairloPage />} />
            <Route path="/:lang/lana-utan-uc/merax" element={<MeraxPage />} />
            <Route path="/:lang/lana-utan-uc/flexkontot" element={<FlexkontotPage />} />
            <Route path="/:lang/lana-utan-uc/brixo" element={<BrixioPage />} />
            <Route path="/:lang/lana-utan-uc/loanstep" element={<LoanstepPage />} />
                    <Route path="/:lang/faq" element={<FAQPage />} />
                    <Route path="/:lang/kontakt" element={<ContactPage />} />
                    <Route path="/:lang/guider/snittranta" element={<SnittrantaPage />} />
                    <Route path="/:lang/guider/handpenning" element={<HandpenningPage />} />
                    <Route path="/:lang/guider/amortera-eller-spara" element={<AmortizationPage />} />
                    <Route path="/:lang/guider/inflation" element={<InflationPage />} />
                    <Route path="/:lang/guider/investeringssparkonto" element={<ISKPage />} />
                    <Route path="/:lang/guider/gavobrev" element={<GavobrevPage />} />
                    <Route path="/:lang/guider/annuitetslån" element={<AnnuitetslanPage />} />
                    <Route path="/:lang/guider/belåningsgrad" element={<BelaningsgradPage />} />
                    <Route path="/:lang/guider/borgenär" element={<BorgenarPage />} />
                    <Route path="/:lang/guider/borgensman" element={<BorgensmanPage />} />
                    <Route path="/:lang/guider/gäldenär" element={<GaldenarPage />} />
                    <Route path="/:lang/guider/pantbrev" element={<PantbrevPage />} />
                    <Route path="/:lang/guider/kreditupplysning" element={<KreditupplysningPage />} />
                    <Route path="/:lang/guider/lanelöfte" element={<LaneloftePage />} />
                    <Route path="/:lang/guider/laneskydd" element={<LaneskyddPage />} />
                    <Route path="/:lang/guider/bankkort-kreditkort" element={<BankkortKreditkortPage />} />
                    <Route path="/:lang/guider/swish" element={<SwishPage />} />
                    <Route path="/:lang/guider/klarna-kop-nu-betala-senare" element={<KlarnaKopNuBetalaSenare />} />
                    <Route path="/:lang/guider/betalkort" element={<BetalkortPage />} />
                    <Route path="/:lang/guider/kontantkort-prepaid" element={<KontantkortPrepaidPage />} />
                    <Route path="/:lang/guider/refinansiering" element={<RefinsieringPage />} />
                    <Route path="/:lang/guider/samlingslån" element={<SamlingslanPage />} />
                    <Route path="/:lang/guider/lan-utan-sakerhet" element={<LanUtanSakerhetPage />} />
                    <Route path="/:lang/guider/reporanta" element={<ReporantaPage />} />
                    <Route path="/:lang/guider/skuldsanering" element={<SkuldsaneringPage />} />
                    <Route path="/:lang/guider/kopekontrakt" element={<KopekontraktPage />} />
                    <Route path="/:lang/guider/kopebrev" element={<KopebrevPage />} />
                    <Route path="/:lang/guider/overbryggningslan" element={<OverbryggninslanPage />} />
                    <Route path="/:lang/guider/foretagslan-aktiebolag" element={<ForetagslanAktiebolagPage />} />
                    <Route path="/:lang/guider/kontantinsats-och-bolanetak" element={<KontantinsatsPage />} />
                    <Route path="/:lang/guider/ranterabatt" element={<RanterabattPage />} />
                    <Route path="/:lang/guider/uppskov" element={<UpskovPage />} />
                    <Route path="/:lang/guider/ranteavdrag" element={<RanteavdragPage />} />
                    <Route path="/:lang/guider/lagfart" element={<LagfartPage />} />
                    <Route path="/:lang/guider/lana-utan-uc" element={<LanaUtanUCPage />} />
                    <Route path="/:lang/guider/effektiv-ranta" element={<EffektivRantaPage />} />
                    <Route path="/:lang/guider/kalp" element={<KALPPage />} />
                    <Route path="/:lang/guider/lanekalkyl" element={<LanekalylPage />} />
                    <Route path="/:lang/guider/budgetkalkyl" element={<BudgetkalylPage />} />
                    <Route path="/:lang/guider/hur-mycket-kan-jag-lana" element={<HurMycketKanJagLanaPage />} />
                    <Route path="/:lang/guider/ranta-rorlig-eller-bunden" element={<RantaRorligEllerBundenPage />} />
                    <Route path="/:lang/klarsprak" element={<KlarsprakPage />} />
                    <Route path="/:lang/verktyg" element={<ToolsPage />} />
                    <Route path="/:lang/verktyg/bolanekalkylator" element={<MortgageCalculatorPage />} />
                    <Route path="/:lang/verktyg/privatlanekalkylator" element={<PrivateLoanCalculatorPage />} />
                    <Route path="/:lang/verktyg/rantekalkylator" element={<InterestRateCalculatorPage />} />
                    <Route path="/:lang/verktyg/kalp-kalkylator" element={<KALPCalculatorPage />} />
                    <Route path="/:lang/om-arsha-josefsson" element={<ArshaJosefssonPage />} />
                    <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </MaintenanceWrapper>
                </ErrorBoundary>
              </TooltipProvider>
            </ComparisonProvider>
          </AuthProvider>
        </CookieConsentProvider>
      </LanguageProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
